"use client";

/**
 * PlaceAutocomplete — Google Places-backed address picker.
 *
 * Replaces the four-field street/city/state/zip grid with a single
 * smart input. Customer types "123 ma" → Google's API streams
 * predictions; pick one and the parent receives a normalized
 * AddressFields object. Used across the customer pickup form, reseller
 * repair form, and admin intake.
 *
 * Falls back gracefully when:
 *   - No `NEXT_PUBLIC_GOOGLE_MAPS_API_KEY` is set (renders a plain
 *     text input + a one-line note pointing to manual fields).
 *   - Maps JS fails to load (network blip / CSP issue) — same plain-
 *     input fallback so the form is never blocked.
 *
 * Restricts predictions to US addresses for now (rivkywigs.com is US-
 * only). Expanding to CA / international is a one-line change.
 */

import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MapPin, Loader2 } from "lucide-react";

export interface PlaceFields {
  street1: string;
  city: string;
  state: string;
  zip: string;
  /** Place ID — keep alongside the human-readable string so two
   *  customers entering the same address dedup cleanly. */
  placeId?: string;
  /** Lat / lng for distance + service-area calcs. */
  lat?: number;
  lng?: number;
}

interface Props {
  value: PlaceFields;
  onChange: (next: PlaceFields) => void;
  /** Optional label above the input. */
  label?: string;
  required?: boolean;
  /** Initial display string in the input — usually `${street1}` after
   *  hydrating from saved address. Not bound bidirectionally; the
   *  user types freely and we only update the parent on a place pick
   *  OR when they manually edit (no place selected). */
  initialDisplay?: string;
  /** Hide the inline expand-to-manual-fields fallback. Used in places
   *  where the parent form wants to render its own city/state/zip
   *  fields below regardless. */
  hideManualToggle?: boolean;
  className?: string;
  placeholder?: string;
}

// Module-level loader — Google's library MUST only be loaded once per
// page. setOptions() must be called before the first importLibrary().
// Subsequent setOptions() calls are no-ops on the same key.
let configured = false;
let placesLib: Promise<google.maps.PlacesLibrary> | null = null;
function loadPlaces(apiKey: string): Promise<google.maps.PlacesLibrary> {
  if (!configured) {
    setOptions({ key: apiKey, v: "weekly" });
    configured = true;
  }
  if (!placesLib) {
    placesLib = importLibrary("places");
  }
  return placesLib;
}

interface Prediction {
  placeId: string;
  primaryText: string;
  secondaryText: string;
}

export default function PlaceAutocomplete({
  value,
  onChange,
  label,
  required,
  initialDisplay,
  hideManualToggle,
  className,
  placeholder,
}: Props) {
  // NEXT_PUBLIC_* must use dot notation — Next.js's webpack inlines
  // these at build time only when accessed by literal property name.
  // Bracket notation compiles to a runtime lookup that's undefined in
  // the browser. Saved memory rule: feedback_nextjs_env_vars.
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const [query, setQuery] = useState<string>(initialDisplay ?? value.street1);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loadingPreds, setLoadingPreds] = useState(false);
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
    null
  );
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mount-time SDK load. Sets the autocomplete + places services on
  // refs; if the load fails we drop into the manual-fields fallback.
  useEffect(() => {
    if (!apiKey) {
      setUnavailable(true);
      return;
    }
    let cancelled = false;
    loadPlaces(apiKey)
      .then((places) => {
        if (cancelled) return;
        autocompleteServiceRef.current = new places.AutocompleteService();
        // PlacesService needs a DOM node — a hidden div suffices.
        const dummy = document.createElement("div");
        placesServiceRef.current = new places.PlacesService(dummy);
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      })
      .catch(() => {
        if (cancelled) return;
        setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Debounced predictions fetch on query change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.trim().length < 3) {
      setPredictions([]);
      return;
    }
    if (!autocompleteServiceRef.current) return;
    setLoadingPreds(true);
    debounceRef.current = setTimeout(() => {
      const svc = autocompleteServiceRef.current;
      if (!svc) return;
      svc.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionTokenRef.current ?? undefined,
          // US-only for now — Rivky Wigs ships domestic. Loosen later
          // if international launches.
          componentRestrictions: { country: "us" },
          types: ["address"],
        },
        (results) => {
          setLoadingPreds(false);
          if (!results) {
            setPredictions([]);
            return;
          }
          setPredictions(
            results.map((p) => ({
              placeId: p.place_id,
              primaryText: p.structured_formatting?.main_text || p.description,
              secondaryText: p.structured_formatting?.secondary_text || "",
            }))
          );
        }
      );
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  function pickPrediction(pred: Prediction) {
    const svc = placesServiceRef.current;
    if (!svc) return;
    svc.getDetails(
      {
        placeId: pred.placeId,
        sessionToken: sessionTokenRef.current ?? undefined,
        fields: ["address_components", "geometry", "formatted_address"],
      },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          return;
        }
        const components = place.address_components || [];
        const get = (type: string) =>
          components.find((c) => c.types.includes(type))?.long_name || "";
        const getShort = (type: string) =>
          components.find((c) => c.types.includes(type))?.short_name || "";
        const streetNumber = get("street_number");
        const route = get("route");
        const street1 = [streetNumber, route].filter(Boolean).join(" ");
        const city =
          get("locality") ||
          get("sublocality") ||
          get("administrative_area_level_3");
        const state = getShort("administrative_area_level_1");
        const zip = get("postal_code");

        const next: PlaceFields = {
          street1,
          city,
          state,
          zip,
          placeId: pred.placeId,
          lat: place.geometry?.location?.lat(),
          lng: place.geometry?.location?.lng(),
        };
        onChange(next);
        setQuery(street1);
        setOpen(false);
        setPredictions([]);
        // Recycle the session token after a successful pick so future
        // queries start a new billing session (Google's per-session
        // pricing requires this for cost optimization).
        if (window.google?.maps?.places?.AutocompleteSessionToken) {
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        }
      }
    );
  }

  // ─── Manual-fields fallback when Maps unavailable ──────────────────
  if (unavailable && !hideManualToggle) {
    return (
      <ManualFields value={value} onChange={onChange} label={label} required={required} />
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          {label}
          {required && " *"}
        </label>
      )}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // Clear the picked place if user edits after picking — they
            // intend to type a new address.
            if (value.placeId) {
              onChange({ ...value, placeId: undefined, lat: undefined, lng: undefined, street1: e.target.value });
            } else {
              onChange({ ...value, street1: e.target.value });
            }
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder ?? "Start typing your address…"}
          className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
          autoComplete="off"
          required={required}
        />
        {loadingPreds && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
        )}
        {open && predictions.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 max-h-64 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg z-30">
            {predictions.map((p) => (
              <button
                key={p.placeId}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickPrediction(p)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900 truncate">{p.primaryText}</div>
                <div className="text-[11px] text-gray-500 truncate">{p.secondaryText}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Show city/state/zip read-only confirmation once a place is picked. */}
      {value.placeId && value.city && (
        <p className="text-[11px] text-emerald-700 mt-1">
          ✓ {value.city}, {value.state} {value.zip}
        </p>
      )}

      {/* Manual-fields escape hatch — admin/customer can override the
          autocomplete entirely if Google didn't find their address. */}
      {!hideManualToggle && !unavailable && (
        <button
          type="button"
          onClick={() => setShowManual((v) => !v)}
          className="mt-1 text-[11px] text-gray-500 hover:text-gray-700 underline"
        >
          {showManual ? "Hide manual entry" : "Address not found? Enter manually"}
        </button>
      )}
      {showManual && (
        <div className="mt-2">
          <ManualFields value={value} onChange={onChange} compact />
        </div>
      )}
    </div>
  );
}

// ─── Manual-fields fallback ───────────────────────────────────────
// Renders the prior 4-field grid so admin can type when Google misses
// an address (rural / new construction / international). Same shape as
// pre-existing pickup forms used.
function ManualFields({
  value,
  onChange,
  label,
  required,
  compact,
}: {
  value: PlaceFields;
  onChange: (next: PlaceFields) => void;
  label?: string;
  required?: boolean;
  compact?: boolean;
}) {
  return (
    <div className={compact ? "space-y-2" : ""}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          {label}
          {required && " *"}
        </label>
      )}
      <input
        type="text"
        value={value.street1}
        onChange={(e) => onChange({ ...value, street1: e.target.value, placeId: undefined })}
        placeholder="Street address"
        required={required}
        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
      />
      <div className="grid grid-cols-3 gap-2 mt-2">
        <input
          type="text"
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value, placeId: undefined })}
          placeholder="City"
          required={required}
          className="col-span-2 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
        />
        <input
          type="text"
          value={value.state}
          onChange={(e) =>
            onChange({ ...value, state: e.target.value.toUpperCase().slice(0, 2), placeId: undefined })
          }
          placeholder="State"
          maxLength={2}
          required={required}
          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
        />
      </div>
      <input
        type="text"
        value={value.zip}
        onChange={(e) => onChange({ ...value, zip: e.target.value, placeId: undefined })}
        placeholder="ZIP"
        required={required}
        className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40 mt-2"
      />
    </div>
  );
}
